import { motion } from 'framer-motion';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';
import './Technologies.css';
import { getApiUrl } from '../utils/api';

const Technologies = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1
  });

  const scrollContainerRef = useRef(null);
  const reviewsScrollRef = useRef(null);
  const [statsRef, statsInView] = useInView({
    triggerOnce: false, // Changed to false to allow re-triggering
    threshold: [0, 0.1, 0.2, 0.3], // Multiple thresholds for better mobile detection
    rootMargin: '0px 0px -100px 0px', // Increased margin for earlier triggering on mobile
    skip: false, // Ensure it's not skipped
    trackVisibility: true, // Track visibility for better mobile support
    delay: 100 // Small delay to ensure proper initialization
  });

  const [apiData, setApiData] = useState({
    title: "Our Services",
    description: "End-to-end technology solutions from design to deployment - tailored to your business needs",
    stats: [
      { value: "22+", label: "Professional's Team" },
      { value: "53+", label: "Satisfied Customers" },
      { value: "120+", label: "Projects Done" },
      { value: "10+", label: "Years of Experience" }
    ],
    data: []
  });
  const [animatedStats, setAnimatedStats] = useState([0, 0, 0, 0]);
  const [hasAnimated, setHasAnimated] = useState(false); // Track if animation has run
  const [forceAnimation, setForceAnimation] = useState(false); // Force animation trigger

  useEffect(() => {
    const url = getApiUrl('/api/services');
    fetch(url)
      .then(res => res.json())
      .then(resData => {
        if (resData && Array.isArray(resData.data) && resData.data.length > 0) {
          setApiData(resData.data[0]);
        }
      })
      .catch(err => console.error("Error fetching services:", err));
  }, []);


  // Detect mobile device for optimized behavior
  const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent) ||
    window.innerWidth <= 768;

  // const scrollReviews = (direction) => {
  //   if (reviewsScrollRef.current) {
  //     const scrollAmount = 400;
  //     reviewsScrollRef.current.scrollBy({
  //       left: direction === 'left' ? -scrollAmount : scrollAmount,
  //       behavior: 'smooth'
  //     });
  //   }
  // };

  // Add fade effect based on scroll position
  useEffect(() => {
    const handleScroll = () => {
      const container = reviewsScrollRef.current;
      if (container) {
        const cards = container.querySelectorAll('.review-card');
        const containerWidth = container.clientWidth;

        cards.forEach((card, index) => {
          const cardRect = card.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const cardLeft = cardRect.left - containerRect.left;
          const cardRight = cardRect.right - containerRect.left;

          // Remove all fade classes
          card.classList.remove('fade-left', 'fade-right', 'center');

          // Check if card is partially off-screen on the left
          if (cardLeft < 0 && cardRight > 0) {
            card.classList.add('fade-left');
          }
          // Check if card is partially off-screen on the right
          else if (cardLeft < containerWidth && cardRight > containerWidth) {
            card.classList.add('fade-right');
          }
          // Card is fully visible
          else if (cardLeft >= 0 && cardRight <= containerWidth) {
            card.classList.add('center');
          }
        });
      }
    };

    const container = reviewsScrollRef.current;
    if (container) {
      container.addEventListener('scroll', handleScroll);
      // Initial call to set correct states
      handleScroll();

      return () => {
        container.removeEventListener('scroll', handleScroll);
      };
    }
  }, []);

  const services = [
    {
      name: 'Flutter',
      image: '/images/searvice/flutter.png',
      color: '#02569B'
    },
    {
      name: 'React',
      image: '/images/searvice/react.png',
      color: '#61DAFB'
    },
    {
      name: 'Node.js',
      image: '/images/searvice/nodejs.png',
      color: '#339933'
    },
    {
      name: 'Golang',
      image: '/images/searvice/Go.png',
      color: '#00ADD8'
    },
    {
      name: 'Python',
      image: '/images/searvice/python.png',
      color: '#3776AB'
    },
    {
      name: 'Java',
      image: '/images/searvice/java.png',
      color: '#007396'
    },
    {
      name: 'AWS',
      image: '/images/searvice/AWS.png',
      color: '#FF9900'
    },
    {
      name: 'GCP',
      image: 'images/searvice/google-cloud.png',
      color: '#4285F4'
    },
    {
      name: 'Azure',
      image: 'images/searvice/Azure.png',
      color: '#0078D4'
    },
    {
      name: 'MongoDB',
      image: '/images/searvice/mongobd.png',
      color: '#47A248'
    },
    {
      name: 'Supabase',
      image: '/images/searvice/supabase.png',
      color: '#3ECF8E'
    },
    {
      name: 'Docker',
      image: '/images/searvice/docker.png',
      color: '#2496ED'
    },
    {
      name: 'Figma',
      image: '/images/searvice/figma.png',
      color: '#F24E1E'
    },
    {
      name: 'Adobe XD',
      image: '/images/searvice/xd.png',
      color: '#FF61F6'
    },
    {
      name: 'Sketch',
      image: '/images/searvice/sketch.png',
      color: '#F7B500'
    },
    {
      name: 'ChatGPT',
      image: '/images/searvice/chatgpt.png',
      color: '#10A37F'
    },
    {
      name: 'Claude AI',
      image: '/images/searvice/claude.png',
      color: '#8A2BE2'
    },
    {
      name: 'Gemini',
      image: '/images/searvice/gemini.png',
      color: '#4285F4'
    },
    {
      name: 'n8n',
      image: '/images/searvice/n8n-color.png',
      color: '#EA4B71'
    }
  ];

  const displayedServices = apiData && Array.isArray(apiData.data) && apiData.data.length > 0
    ? apiData.data.map(item => {
      const match = services.find(s => s.name.toLowerCase() === item.iconName.toLowerCase());
      return {
        name: item.iconName,
        image: item.iconUrl,
        color: match ? match.color : '#61DAFB'
      };
    })
    : services;

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.15
      }
    }
  };

  const itemVariants = {
    hidden: { scale: 0, opacity: 0 },
    visible: {
      scale: 1,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 100,
        damping: 12
      }
    }
  };

  // Auto-scroll effect - infinite scrolling
  useEffect(() => {
    const scrollContainer = scrollContainerRef.current;
    if (!scrollContainer) return;

    const scrollSpeed = 0.5; // Slower, smoother animation
    let animationFrameId;
    let isScrolling = true;

    const autoScroll = () => {
      if (scrollContainer && isScrolling) {
        scrollContainer.scrollLeft += scrollSpeed;

        // Calculate the width of one set of services (we have 3 copies)
        const singleSetWidth = scrollContainer.scrollWidth / 3;

        // Reset scroll when we've scrolled past the first set
        if (scrollContainer.scrollLeft >= singleSetWidth) {
          scrollContainer.scrollLeft = 0;
        }

        animationFrameId = requestAnimationFrame(autoScroll);
      }
    };

    // Start the animation
    animationFrameId = requestAnimationFrame(autoScroll);

    // Pause on hover for better UX
    const handleMouseEnter = () => {
      isScrolling = false;
    };

    const handleMouseLeave = () => {
      isScrolling = true;
      animationFrameId = requestAnimationFrame(autoScroll);
    };

    scrollContainer.addEventListener('mouseenter', handleMouseEnter);
    scrollContainer.addEventListener('mouseleave', handleMouseLeave);

    return () => {
      if (animationFrameId) {
        cancelAnimationFrame(animationFrameId);
      }
      scrollContainer.removeEventListener('mouseenter', handleMouseEnter);
      scrollContainer.removeEventListener('mouseleave', handleMouseLeave);
    };
  }, []);

  // Animation function with improved mobile performance
  const animateValue = useCallback((start, end, duration, setter) => {
    const range = end - start;
    const startTime = performance.now();

    const animate = (currentTime) => {
      const elapsed = currentTime - startTime;
      const progress = Math.min(elapsed / duration, 1);

      // Use easing function for smoother animation
      const easeOutCubic = 1 - Math.pow(1 - progress, 3);
      const current = start + (range * easeOutCubic);

      setter(Math.floor(current));

      if (progress < 1) {
        requestAnimationFrame(animate);
      } else {
        setter(end);
      }
    };

    requestAnimationFrame(animate);
  }, []);

  // Start animation function
  const startCountAnimation = useCallback(() => {
    if (hasAnimated) return;

    setHasAnimated(true);

    const statsArray = apiData && Array.isArray(apiData.stats) ? apiData.stats : [];
    const targetValues = statsArray.map(stat => {
      const num = parseInt(stat.value, 10);
      return isNaN(num) ? 0 : num;
    });

    // Ensure animatedStats has the correct length initialized to 0
    setAnimatedStats(new Array(statsArray.length).fill(0));

    // Add a small delay to ensure the component is fully rendered
    const startAnimation = setTimeout(() => {
      targetValues.forEach((val, idx) => {
        animateValue(0, val, 2000, (newVal) => {
          setAnimatedStats(prev => {
            const next = [...prev];
            next[idx] = newVal;
            return next;
          });
        });
      });
    }, 100);

    return () => {
      clearTimeout(startAnimation);
    };
  }, [hasAnimated, animateValue, apiData]);

  // Animated counter effect with improved reliability
  useEffect(() => {
    // Trigger animation if stats are in view or force animation is true
    if ((statsInView || forceAnimation) && !hasAnimated) {
      startCountAnimation();
    }
  }, [statsInView, forceAnimation, hasAnimated, startCountAnimation]);

  // Additional mobile-specific trigger using scroll position
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile devices

    const handleScroll = () => {
      const statsElement = statsRef.current;
      if (!statsElement || hasAnimated) return;

      const rect = statsElement.getBoundingClientRect();
      const windowHeight = window.innerHeight;

      // Check if the stats section is visible (more lenient for mobile)
      const isVisible = rect.top < windowHeight * 0.8 && rect.bottom > 0;

      if (isVisible) {
        // console.log('Scroll-based animation triggered on mobile');
        setForceAnimation(true);
      }
    };

    // Add scroll listener for mobile fallback
    window.addEventListener('scroll', handleScroll, { passive: true });

    return () => {
      window.removeEventListener('scroll', handleScroll);
    };
  }, [hasAnimated, isMobile]);

  // Fallback mechanism - trigger animation after component mount if not already triggered
  useEffect(() => {
    const fallbackDelay = isMobile ? 1000 : 1500; // Faster fallback on mobile
    const fallbackTimer = setTimeout(() => {
      if (!hasAnimated) {
        // console.log('Fallback animation triggered');
        setForceAnimation(true);
      }
    }, fallbackDelay);

    return () => clearTimeout(fallbackTimer);
  }, [hasAnimated, isMobile]);

  // Mobile-specific visibility detection using Intersection Observer API directly
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile devices

    const statsElement = statsRef.current;
    if (!statsElement) return;

    // Create a more aggressive Intersection Observer for mobile
    const mobileObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasAnimated) {
            // console.log('Mobile Intersection Observer triggered');
            setForceAnimation(true);
          }
        });
      },
      {
        threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5],
        rootMargin: '0px 0px -50px 0px'
      }
    );

    mobileObserver.observe(statsElement);

    return () => {
      mobileObserver.disconnect();
    };
  }, [hasAnimated, isMobile]);

  // Page Visibility API support - trigger animation when page becomes visible
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (!document.hidden && !hasAnimated) {
        // console.log('Page became visible, triggering animation');
        setForceAnimation(true);
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [hasAnimated]);

  // Touch-based trigger for mobile devices
  useEffect(() => {
    if (!isMobile) return; // Only apply on mobile devices

    const handleTouch = () => {
      if (!hasAnimated) {
        // console.log('Touch-based animation triggered on mobile');
        setForceAnimation(true);
      }
    };

    // Add touch event listeners
    document.addEventListener('touchstart', handleTouch, { passive: true });
    document.addEventListener('touchend', handleTouch, { passive: true });

    return () => {
      document.removeEventListener('touchstart', handleTouch);
      document.removeEventListener('touchend', handleTouch);
    };
  }, [hasAnimated, isMobile]);

  // Reset animation state when stats go out of view (for re-triggering)
  useEffect(() => {
    if (!statsInView && hasAnimated) {
      // Reset after a delay to allow for smooth re-entry
      const resetTimer = setTimeout(() => {
        setHasAnimated(false);
        const len = apiData && Array.isArray(apiData.stats) ? apiData.stats.length : 0;
        setAnimatedStats(new Array(len).fill(0));
      }, 1000);

      return () => clearTimeout(resetTimer);
    }
  }, [statsInView, hasAnimated, apiData]);

  return (
    <section id="technologies" className="technologies" ref={ref}>
      <motion.div
        className="technologies-container"
        variants={containerVariants}
        initial="hidden"
        animate={inView ? "visible" : "hidden"}
      >
        <motion.div className="section-header-1" variants={itemVariants}>
          <h2 className="services-title">{apiData.title}</h2>
          <p className="section-subtitle-1">
            {apiData.description}
          </p>
        </motion.div>

        <div className="tech-scroll-wrapper">
          <div
            className="tech-scroll-container"
            ref={scrollContainerRef}
          >
            {/* Duplicate services for seamless infinite scroll */}
            {[...displayedServices, ...displayedServices, ...displayedServices].map((service, index) => (
              <motion.div
                key={index}
                className="tech-card"
                variants={itemVariants}
                initial="hidden"
                animate={inView ? "visible" : "hidden"}
                style={{ '--tech-color': service.color }}
              >
                <div className="tech-icon">
                  <img
                    src={service.image}
                    alt={service.name}
                    className="tech-image"
                  />
                </div>
                <h3 className="tech-name">{service.name}</h3>
              </motion.div>
            ))}
          </div>
        </div>

        <motion.div className="tech-stats" variants={itemVariants} ref={statsRef}>
          {apiData.stats && apiData.stats.map((stat, index) => {
            const suffix = stat.value.replace(/[0-9]/g, '');
            const delay = 0.2 * (index + 1);
            return (
              <React.Fragment key={index}>
                {index > 0 && <div className="stat-divider"></div>}
                <div className="stat-item">
                  <motion.div
                    className="stat-number"
                    initial={{ opacity: 0, scale: 0 }}
                    animate={(statsInView || forceAnimation) ? { opacity: 1, scale: 1 } : { opacity: 0, scale: 0 }}
                    transition={{ duration: 0.5, delay: delay }}
                  >
                    {animatedStats[index] !== undefined ? animatedStats[index] : 0}{suffix}
                  </motion.div>
                  <div className="stat-label">{stat.label}</div>
                </div>
              </React.Fragment>
            );
          })}
        </motion.div>

        {/* Client Reviews Section */}

      </motion.div>
    </section>
  );
};

export default Technologies;

